import { useState } from 'react'

import { Box, Button, Checkbox, FormControlLabel, FormHelperText, TextField } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'

import api from 'common/api'
import PhoneNumberInput from 'components/PhoneNumberInput'
import Title from 'components/Title'

import Confirmation from './Confirmation'

const useStyles = makeStyles((theme) => ({
  container: {
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  chipGroup: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  formError: {
    textAlign: 'center',
    marginTop: theme.spacing(-1), // Reduce space between button and error message
  },
}))

const decodeToken = (token: string) => {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace('-', '+').replace('_', '/')
  return JSON.parse(window.atob(base64))
}

const MAX_ADDITIONAL_INFO_LENGTH = 300
const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required.'),
  lastName: Yup.string().required('Last name is required.'),
  title: Yup.string().required('Job title is required.'),
  phoneNumber: Yup.string().required('Phone number is required.'),
  additionalInfo: Yup.string().max(
    MAX_ADDITIONAL_INFO_LENGTH,
    `Additional information must be ${MAX_ADDITIONAL_INFO_LENGTH} characters or less.`,
  ),
  subscribe: Yup.boolean(),
})

type RequestAOAccountValues = {
  email: string
  firstName: string
  lastName: string
  title: string
  phoneNumber: string
  phoneExtension: string
  additionalInfo: string
  subscribe: boolean
}

type RequestAOAccountProps = {
  token: string
}
const RequestAOAccount = ({ token }: RequestAOAccountProps) => {
  const classes = useStyles()
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const decodedToken = decodeToken(token)
  const isExpired = decodedToken.exp < Math.floor(Date.now() / 1000)
  if (!decodedToken || !decodedToken.Email || !decodedToken.Scid || !decodedToken.AccessType || isExpired) {
    // TODO: Show a more user-friendly error message here.
    return (
      <Title
        title="Whoops!"
        subtitle={<div>Invalid or expired token. Please request a new invitation.</div>}
      />
    )
  }

  const initialValues: RequestAOAccountValues = {
    email: decodedToken.Email || '',
    // User input values
    firstName: '',
    lastName: '',
    title: '',
    phoneNumber: '',
    phoneExtension: '',
    additionalInfo: '',
    subscribe: false,
  }

  const onSubmit = (values: any) => {
    setSubmitError('')
    const payload = {
      FirstName: values.firstName,
      LastName: values.lastName,
      Title: values.title,
      Phone: values.phoneNumber,
      PhoneExtension: values.phoneExtension,
      AdditionalInfo: values.additionalInfo,
      Subscribe: values.subscribe,
      Token: token,
    }

    return api
      .post('/api/createAO', payload)
      .then(() => {
        console.log('success!')
        setSubmitted(true)
      })
      .catch(() => setSubmitError('Something went wrong. Please try again later.'))
  }

  if (submitted) {
    return <Confirmation collegeName={decodedToken.CollegeName} />
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={(values) => onSubmit(values)}
    >
      {({ errors, touched, isValid, isSubmitting, values, setFieldValue }) => (
        <>
          <Title
            title="Just a few more details"
            subtitle={
              <div>
                We’ll submit a request to your admin at {decodedToken.CollegeName || 'your institution'} to set up your
                account with <strong>{values.email}</strong>
              </div>
            }
          />
          <Form>
            <div className={classes.container}>
              <Field
                as={TextField}
                variant="outlined"
                label="First Name"
                name="firstName"
                required
                error={touched.firstName && Boolean(errors.firstName)}
                helperText={touched.firstName && errors.firstName}
              />
              <Field
                as={TextField}
                variant="outlined"
                label="Last Name"
                name="lastName"
                required
                error={touched.lastName && Boolean(errors.lastName)}
                helperText={touched.lastName && errors.lastName}
              />
              <Field
                as={TextField}
                variant="outlined"
                label="Job Title"
                name="title"
                required
                error={touched.title && Boolean(errors.title)}
                helperText={touched.title && errors.title}
              />
              <Box
                display="flex"
                gridGap={8}
              >
                <PhoneNumberInput
                  name="phoneNumber"
                  label="Work Phone Number"
                  required
                />
                <Field
                  as={TextField}
                  variant="outlined"
                  label="Ext."
                  name="phoneExtension"
                />
              </Box>
              <Field
                as={TextField}
                multiline
                rows={4}
                variant="outlined"
                label="Additional Information (Optional)"
                name="additionalInfo"
                error={values.additionalInfo?.length > MAX_ADDITIONAL_INFO_LENGTH}
                helperText={`${MAX_ADDITIONAL_INFO_LENGTH - values.additionalInfo?.length} characters left`}
              />
              <FormControlLabel
                label="I'd like to receive updates about Scoir's products and services. "
                control={
                  <Field
                    id="subscribe"
                    name="subscribe"
                    as={Checkbox}
                    disableRipple={false}
                    color="primary"
                    checked={values.subscribe}
                    onChange={() => setFieldValue('subscribe', !values.subscribe, true)}
                    onKeyPress={(event: any) => {
                      if (event.key === 'Enter') {
                        setFieldValue('subscribe', !values.subscribe, true)
                      }
                    }}
                  />
                }
              />
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={!isValid || isSubmitting}
              >
                Submit Account Request
              </Button>
              {submitError && (
                <FormHelperText
                  error
                  className={classes.formError}
                >
                  {submitError}
                </FormHelperText>
              )}
            </div>
          </Form>
        </>
      )}
    </Formik>
  )
}

export default RequestAOAccount
